import type { SortDirection, SortField } from "feeds/SortToggle.types";
import { useCallback, useState } from "react";
import type { DateDisplayMode } from "ui/DateDisplay";

type UseSortStateOptions = {
  defaultField?: SortField;
  defaultDirection?: SortDirection;
};

type UseSortStateResult = {
  sortField: SortField;
  sortDirection: SortDirection;
  /** Режим даты на карточке, соответствующий полю сортировки. */
  dateDisplayMode: DateDisplayMode;
  /** Обработчик для `SortToggle.onSortChange` (третий аргумент groupByCase игнорируется). */
  onSortChange: (field: SortField, direction: SortDirection) => void;
};

/**
 * Держит порядок сортировки в локальном состоянии (в отличие от URL-версии
 * [useSortParam]) — для клиентской сортировки без пагинации, где порядок не нужно
 * хранить в адресе. Отдаёт поле/направление, `dateDisplayMode` и обработчик смены.
 */
export const useSortState = ({
  defaultField = "dateModified",
  defaultDirection = "desc",
}: UseSortStateOptions = {}): UseSortStateResult => {
  const [sortField, setSortField] = useState<SortField>(defaultField);
  const [sortDirection, setSortDirection] =
    useState<SortDirection>(defaultDirection);

  const onSortChange = useCallback(
    (field: SortField, direction: SortDirection) => {
      setSortField(field);
      setSortDirection(direction);
    },
    [],
  );

  const dateDisplayMode: DateDisplayMode =
    sortField === "dateCreated" ? "created" : "modified";

  return { sortField, sortDirection, dateDisplayMode, onSortChange };
};
